'use strict';

import Spinner from 'Components/VZSpinner';

const template = document.createElement('template');
template.innerHTML = `
    <script src="https://www.youtube.com/iframe_api" defer="true"></script>
    <style>
    .d-none {
        display: none;
    }
    ::host,
    .video-card {
        background-position: center center;
        background-size: cover;
        overflow: hidden;
        padding-bottom: 56.25%;
        position: relative;
        width: 100%;
    }
    .video-card iframe {
        left: 50%;
        position: absolute;
        top: 50%;
        transform: translate3d(-50%, -50%, 0);
    }
    </style>
    <div class="video-card mb-4 bg-black" id="c${Math.random(Date.now()).toString().replace('0.', '')}">
        <div class="d-none"></div>
    </div>
    <div>
        <slot name="play-button"/>
    </div>
`;

/**
 * @private loadYouTubeAPI
 */
function loadYouTubeAPI() {
    if (window.onYouTubeIframeAPIReady) return;
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    tag.defer = true;
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    /* global YT */
}

/**
 * @export
 * @class YouTube
 * @extends {HTMLElement}
 *
 * @example
 * <vz-video id="_4kHxtiuML0" autoplay="false" controls="true" preview-seconds="3">
 */
export default class VZYoutube extends HTMLElement {
    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
        this.shadowRoot.appendChild(template.content.cloneNode(true));

        if (this.getAttribute('poster')) {
            this.shadowRoot.querySelector('.video-card').setAttribute('style', `background-image: url('${this.getAttribute('poster')}');`);
        }
    }

    connectedCallback() {
        this.playerOptions = {
            videoId: this.getAttribute('id'),
            playerVars: {
                origin: 'location.origin',
                autoplay: this.getAttribute('autoplay'),
                mute: this.getAttribute('autoplay'),
                controls: this.getAttribute('controls'),
                previewSeconds: this.getAttribute('preview-seconds')
            },
            events: {
                onReady: this.onReady.bind(this),
                onStateChange: this.onStateChange.bind(this),
                onError: this.onError.bind(this)
            }
        };

        if (window.onYouTubeIframeAPIReady && YT) {
            this.initPlayer();
        } else {
            loadYouTubeAPI();
            window.onYouTubeIframeAPIReady = () => [...document.querySelectorAll('vz-video')].forEach((video) => video.initPlayer());
        }
    }

    initPlayer() {
        const container = this.shadowRoot.querySelector('.video-card').firstElementChild;
        this.player = new YT.Player(container, this.playerOptions);

        this.player.setSize(this.parentElement.offsetWidth, this.parentElement.offsetHeight);
        this.player.playerOptions = this.playerOptions;
        this.spinner = new Spinner();
        this.playButton = null;

        const sibling = this.closest('.js-video-container')?.nextElementSibling;

        if (sibling && sibling.classList.contains('js-playbutton-container')) {
            this.playButton = sibling;
            this.playButton.addEventListener('click', this.playVideo.bind(this));
        }
        window.addEventListener('popstate', this.player.stopVideo);
    }

    onStateChange(event) {
        // If the video is starting for the first time:
        // pause video after X seconds if previewSeconds is defined
        if (event.data === YT.PlayerState.UNSTARTED && this.player.playerOptions.playerVars.previewSeconds) {
            setTimeout(() => {
                if ('pauseVideo' in this.player && !this.player.userInit) {
                    this.player.pauseVideo();
                }
            }, this.player.playerOptions.playerVars.previewSeconds * 1000);

            // Make the video visible
        } else if (event.data === YT.PlayerState.PLAYING) {
            this.spinner.stop();
            this.shadowRoot.querySelector('iframe').classList.remove('d-none');
            if (this.playButton) this.playButton.classList.add('d-none');
        }

        // Show poster after pause or end
        if ((event.data === YT.PlayerState.PAUSED || event.data === YT.PlayerState.ENDED) && this.hasAttribute('poster')) {
            this.shadowRoot.querySelector('iframe').classList.add('d-none');
            if (this.playButton) this.playButton.classList.remove('d-none');
        }

        // Allow restart when ended
        if (event.data === YT.PlayerState.ENDED && this.hasAttribute('restart-label')) {
            this.playButton.classList.add('restart');
            this.playButton.textContent = this.getAttribute('restart-label');
        }
    }

    onReady() {
        // Video must be "unavailable"
        if (this.player.getDuration() === 0) {
            if (this.playButton) {
                this.playButton.remove();
            }
        } else if (this.player.playerOptions.autoplay === 1) {
            this.player.playVideo();
        }
    }

    onError() {
        if (this.playButton) {
            this.playButton.remove();
        }
        this.player.destroy();
    }

    playVideo() {
        this.spinner.start();
        this.playButton.classList.add('d-none');
        this.player.userInit = true;
        this.player.playVideo();
    }
}

if (!window.customElements.get('vz-video')) {
    window.customElements.define('vz-video', VZYoutube);
}
